import type { MouseEvent as ReactMouseEvent, KeyboardEvent } from 'react';
import type { FindingValueSource } from '@/models/report';
import { useState, useCallback, useRef, useEffect } from 'react';
import { navigateWorksheetTab } from '../../../shared/utils/tabNavigation';
import styles from './StatementRow.module.css';

/** AI statements get the same green ✦ as AI measurements and tags, in a
 *  gutter left of the text so every statement starts at the same edge. The
 *  product is only in the screen-reader label. SR-graded and reader-written statements
 *  get no mark: a reader's own wording needs no label. */
const AI_PRODUCTS: Partial<Record<FindingValueSource, string>> = {
  CARDIOVISION_V2: 'CVv2',
  ECHOMEASURE_V2: 'EMv2',
};
const AI_MARK = '✦';

interface StatementRowProps {
  text: string;
  valueSource?: FindingValueSource | null;
  index: number;
  onSave: (index: number, newText: string) => void;
  onDelete: (index: number) => void;
  /** When set, the source badge is a button that opens the clips the AI
   *  prediction was made on (AP-965). */
  onOpenClips?: () => void;
  disabled?: boolean;
}

/**
 * One statement line. "Editing" is nothing more than the textarea having
 * focus: there is no stored edit flag, so two rows can never both look
 * focused. The buffer commits on blur and follows the committed text whenever
 * it changes from outside (undo / redo), so a later blur can't re-save a
 * stale edit.
 */
export default function StatementRow({
  text,
  valueSource,
  index,
  onSave,
  onDelete,
  onOpenClips,
  disabled = false,
}: StatementRowProps) {
  const [editValue, setEditValue] = useState(text);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => setEditValue(text), [text]);

  // Auto-resize to fit content without scrollbars
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [editValue]);

  const handleBlur = useCallback(() => {
    const trimmed = editValue.trim();
    if (!trimmed) {
      onDelete(index);
    } else {
      onSave(index, trimmed);
    }
  }, [editValue, index, onDelete, onSave]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Tab') {
        e.preventDefault();
        navigateWorksheetTab(textareaRef.current!, e.shiftKey);
        return;
      }
      if (e.key === 'Backspace' && editValue === '') {
        e.preventDefault();
        const container = textareaRef.current?.closest('[data-finding-input]');
        const focusables = Array.from(
          container?.querySelectorAll<HTMLElement>('textarea, input') ?? []
        );
        const idx = focusables.indexOf(textareaRef.current!);
        const prevEl = idx > 0 ? focusables[idx - 1] : null;
        onDelete(index);
        if (prevEl) setTimeout(() => prevEl.focus(), 0);
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        textareaRef.current?.blur();
      } else if (e.key === 'Escape') {
        textareaRef.current?.blur();
      }
    },
    [editValue, index, onDelete]
  );

  const handleDeleteClick = useCallback(
    (e: ReactMouseEvent) => {
      e.stopPropagation();
      onDelete(index);
    },
    [index, onDelete]
  );

  const product = valueSource ? AI_PRODUCTS[valueSource] : undefined;
  const markLabel = `AI (${product})`;

  return (
    <div className={styles.row}>
      <span className={styles.gutter}>
        {product &&
          (onOpenClips ? (
            <button
              type="button"
              tabIndex={-1}
              className={`${styles.aiMark} ${styles.aiMarkButton}`}
              aria-label={`${markLabel}, show its clips`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={onOpenClips}
            >
              {AI_MARK}
            </button>
          ) : (
            <span className={styles.aiMark} role="img" aria-label={markLabel}>
              {AI_MARK}
            </span>
          ))}
      </span>
      <textarea
        ref={textareaRef}
        className={`${styles.input} ${disabled ? styles.inputDisabled : ''}`}
        value={editValue}
        data-committed={text}
        readOnly={disabled}
        rows={1}
        onFocus={disabled ? undefined : (e) => e.target.select()}
        onChange={disabled ? undefined : (e) => setEditValue(e.target.value)}
        onBlur={disabled ? undefined : handleBlur}
        onKeyDown={disabled ? undefined : handleKeyDown}
      />
      {!disabled && (
        <button
          type="button"
          tabIndex={-1}
          className={styles.deleteButton}
          onClick={handleDeleteClick}
          aria-label="Remove statement"
        >
          &times;
        </button>
      )}
    </div>
  );
}
